/**
 * Runs one write per selected contact and says which ones failed (W4,
 * B2-liste, DES-12). The list's bulk actions used to swallow each error and
 * toast « mis à jour » regardless; the caller now keeps the failed ids
 * selected and offers « Réessayer ».
 *
 * Sequential by default, as before: a 300-contact selection must not fire 300
 * concurrent requests into the rate limiter (RUI-12). A very large run (a
 * whole list, up to 20 000 contacts) may use a small pool (`concurrency`,
 * ~5): never an unbounded `Promise.all` (REV-W4-F2).
 */
export interface BulkResult { ok: string[]; failed: string[] }

export async function runBulk(
  ids: readonly string[],
  op: (id: string) => Promise<unknown>,
  /** An error that means « already done » (a 404 on delete) counts as a success. */
  isAlreadyDone: (err: unknown) => boolean = () => false,
  { concurrency = 1 }: { concurrency?: number } = {},
): Promise<BulkResult> {
  // One outcome per index, so the result keeps the input order whatever the pool.
  const done: boolean[] = new Array(ids.length);
  let next = 0;
  const worker = async () => {
    while (next < ids.length) {
      const i = next;
      next += 1;
      try {
        await op(ids[i] as string);
        done[i] = true;
      } catch (err) {
        done[i] = isAlreadyDone(err);
      }
    }
  };
  const size = Math.max(1, Math.min(Math.floor(concurrency), ids.length));
  await Promise.all(Array.from({ length: size }, worker));
  const ok: string[] = [];
  const failed: string[] = [];
  ids.forEach((id, i) => { (done[i] ? ok : failed).push(id); });
  return { ok, failed };
}

/** A 404 from the API client: the record is already gone. */
export function isNotFound(err: unknown): boolean {
  const e = err as { status?: number; message?: string } | null;
  return e?.status === 404 || String(e?.message ?? err).includes('404');
}
